import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AccountShell } from "../components/account/AccountShell";
import { PlatformTile } from "../components/account/PlatformTile";
import { ProfileAvatar } from "../components/account/ProfileAvatar";
import { WorkspaceSwitcher } from "../components/account/WorkspaceSwitcher";
import { CreateWorkspaceModal } from "../components/CreateWorkspaceModal";
import { useLastWorkspace } from "../hooks/useLastWorkspace";
import { useWorkspaces, workspacesQueryOptions } from "../hooks/useWorkspaces";
import { authClient } from "../lib/auth-client";
import { setLastWorkspaceId } from "../lib/last-workspace";

export const Route = createFileRoute("/")({
  beforeLoad: async ({ context }) => {
    const { data: session } = await authClient.getSession();
    if (!session) {
      throw redirect({ to: "/login" });
    }
    await context.queryClient.ensureQueryData(workspacesQueryOptions());
    return { session };
  },
  component: AccountConsolePage,
});

const appBase = import.meta.env.VITE_APP_URL ?? "https://app.usesondersoft.com";

function AccountConsolePage() {
  const { session } = Route.useRouteContext();
  const [profileImage, setProfileImage] = useState<string | null | undefined>(
    session.user.image,
  );

  useEffect(() => {
    setProfileImage(session.user.image);
  }, [session.user.image]);

  const { data: workspacesResult, isLoading } = useWorkspaces();

  const workspaces = workspacesResult?.success ? workspacesResult.data : [];
  const { workspace, setWorkspace, hasMultiple } = useLastWorkspace(workspaces);
  const needsWorkspace = !isLoading && workspaces.length === 0;
  const userName = session.user.name ?? "there";
  const hasWorkspace = Boolean(workspace);

  const platforms: Array<{
    label: string;
    icon: string;
    path: string;
    disabled: boolean;
    title?: string;
  }> = [
    {
      label: "SonderSoft Support",
      icon: "/support.svg",
      path: `/support/${workspace?.id}`,
      disabled: !hasWorkspace || workspace?.plan === "starter",
      title:
        workspace?.plan === "starter"
          ? "Available on Growth plan and above"
          : undefined,
    },
    {
      label: "SonderSoft Knowledge",
      icon: "/kb.svg",
      path: `/knowledge/${workspace?.id}`,
      disabled: !hasWorkspace,
    },
  ];

  return (
    <AccountShell>
      <div className="flex flex-1 flex-col items-center mt-32">
        <div className="flex w-full max-w-md flex-col items-center text-center">
          <ProfileAvatar
            className="mb-5 sm:mb-6"
            imageUrl={profileImage}
            onImageUpdated={(url) => {
              setProfileImage(url);
              void authClient.getSession();
            }}
          />

          <p className="mb-8 text-lg font-medium text-foreground sm:mb-10 sm:text-xl">
            Hello, {userName}
          </p>

          {hasMultiple && workspace && (
            <div className="mb-8 w-full text-left sm:mb-10">
              <p className="mb-3 text-xs font-medium tracking-wide text-foreground/40">
                Active workspace
              </p>
              <WorkspaceSwitcher
                workspaces={workspaces}
                value={workspace}
                onChange={setWorkspace}
              />
            </div>
          )}

          <div className="w-full">
            <p className="mb-4 text-xs font-medium tracking-wide text-foreground/40 sm:mb-5 text-left">
              Platforms
            </p>
            <div className="grid grid-cols-2 gap-12 sm:flex sm:justify-center sm:gap-12">
              {platforms.map((platform) => (
                <PlatformTile
                  key={platform.label}
                  label={platform.label}
                  icon={platform.icon}
                  href={
                    platform.disabled
                      ? undefined
                      : `${appBase}${platform.path}`
                  }
                  onNavigate={
                    platform.disabled || !workspace
                      ? undefined
                      : () => setLastWorkspaceId(workspace.id)
                  }
                  disabled={platform.disabled}
                  title={platform.title}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <CreateWorkspaceModal open={needsWorkspace} />
    </AccountShell>
  );
}
